import {
  BiodiversityIcon,
  cn,
  Co2Icon,
  ForestryIcon,
  Loader,
  Pill,
  RiArrowRightSLine,
  Stack,
  StackProps,
  WaterIcon,
} from 'component-library';
import { FC, HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { Plot, PlotStatusEnum, PlotType } from '@/api/rest/resources/types/plot';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { MaybeLink, PlotTypeIcon, SuspenseBoundary, withSuspenseBoundary } from '@/components';
import { MapPopup, MapPopupContent, MapPopupProps } from '@/components/MapOverview/components/MapPopup';
import { PlotThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { PendingPill } from '@/components/Pill/PendingPill';
import { useFact } from '@/hooks/useFact';
import { usePlotLocation } from '@/hooks/usePlotLocation';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { useDestinationPath } from '@/pages/shared/plots/hooks/useDestinationPath';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';
import { squareMetersToHectares } from '@/utils/plot';
import { getColorOfPill, getTextColorOfPill } from '@/utils/plot/get-color-of-plot-status/getColorOfPlotStatus';

export type ProjectMapPopupProps = MapPopupProps;

export const ProjectMapPopup = ({ plot, onClose }: ProjectMapPopupProps) => {
  return (
    <MapPopup plot={plot} onClose={onClose}>
      <MapPopupContent>
        <ProjectMapPopupContent plot={plot} onClose={onClose} />
      </MapPopupContent>
    </MapPopup>
  );
};

export const ProjectMapPopupContent = withSuspenseBoundary(
  ({ plot, onClose }: ProjectMapPopupProps) => {
    const { getDestinationPath } = useDestinationPath();
    const destinationPath = getDestinationPath(plot);

    return (
      <MapPopup plot={plot} onClose={onClose}>
        <MapPopupContent>
          <div className='p-2'>
            <MaybeLink to={destinationPath}>
              <Stack direction='row' centerMain className='rounded-[10px] p-2 full-bleed-x hover:bg-neutral-hover'>
                <Stack direction='row' centerMain spacing={4} className='flex-1'>
                  <PlotThumbnail plot={plot} className='h-[72px] w-[72px] rounded-md' />
                  <InfoBox className='min-w-0 max-w-[250px] items-start gap-2'>
                    <InfoText className='max-w-full truncate'>{plot.name}</InfoText>
                  </InfoBox>
                </Stack>
                <RiArrowRightSLine size={24} className='shrink-0' />
              </Stack>
            </MaybeLink>
          </div>
          <SuspenseBoundary
            fallback={
              <Stack center className='h-[20px]'>
                <Loader />
              </Stack>
            }
          >
            <PlotInfo plot={plot} />
          </SuspenseBoundary>

          <Stack spacing={6} className='px-4 pb-4'>
            {![PlotStatusEnum.invalid, PlotStatusEnum.new_plot].includes(plot.status) && <PlotFacts {...plot} />}
          </Stack>
        </MapPopupContent>
      </MapPopup>
    );
  },
  <Loader className='min-h-[300px]' />,
);

const PlotInfo = ({ plot }: Pick<ProjectMapPopupProps, 'plot'>) => {
  const { t } = useTranslation();

  const plotLocation = usePlotLocation({
    plotId: plot.id,
  });

  const { display: areaDisplay } = useFact({
    value: squareMetersToHectares(plot.area),
    unit: UnitEnum.ha,
  } as PlotReportFactElement);

  return (
    <Stack direction='row' spacing={10} className='px-4'>
      <InfoBox className='flex-shrink-0'>
        <InfoTitle>{t('global.plot.mapPopup.labels.type')}</InfoTitle>
        <PlotTypeIcon type={plot.type} />
      </InfoBox>
      <InfoBox className='flex-shrink-0'>
        <InfoTitle>{t('global.plot.mapPopup.labels.area')}</InfoTitle>
        <InfoText>{areaDisplay}</InfoText>
      </InfoBox>
      <InfoBox>
        <InfoTitle>{t('global.plot.mapPopup.labels.location')}</InfoTitle>
        <InfoText>{plotLocation}</InfoText>
      </InfoBox>
    </Stack>
  );
};

export const PlotFacts = (plot: Plot) => {
  const { t } = useTranslation();

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (plot.type) {
    case PlotType.TREE_COVER: // TODO: MVP-4724 - Change PlotType.TREE_COVER to PlotType.FORESTRY
      return (
        <>
          <InfoBox>
            <InfoTitle>{t('global.analysis.aboveGroundCarbon')}</InfoTitle>
            <AboveGroundCarbonStorage {...plot} />
          </InfoBox>
          <InfoBox>
            <InfoTitle>{t('global.analysis.waterHoldingCapacity')}</InfoTitle>
            <WaterHoldingCapacity {...plot} />
          </InfoBox>
          <InfoBox>
            <InfoTitle>{t('global.analysis.canopyCover')}</InfoTitle>
            <CanopyCover {...plot} />
          </InfoBox>
          <InfoBox>
            <InfoTitle>{t('global.analysis.canopyHeight')}</InfoTitle>
            <CanopyHeight {...plot} />
          </InfoBox>
        </>
      );
    default:
      return (
        <>
          <InfoBox>
            <InfoTitle>{t('global.analysis.carbonStorageBg')}</InfoTitle>
            <CarbonStorage {...plot} />
          </InfoBox>
          <InfoBox>
            <InfoTitle>{t('global.analysis.waterHoldingCapacity')}</InfoTitle>
            <WaterHoldingCapacity {...plot} />
          </InfoBox>
          <InfoBox>
            <InfoTitle>{t('global.analysis.protectedOnFarmHabitat')}</InfoTitle>
            <BiodiversityZone {...plot} />
          </InfoBox>
        </>
      );
  }
};

const BaseInfoPill = ({ plot, plotFact, icon }: { plot: Plot; plotFact: PlotReportFactElement; icon: ReactNode }) => {
  const { t } = useTranslation();

  let { display } = useFact(plotFact);
  display = display === 'N/A' ? '--' : display;
  const isOutdatedValue = display !== '--' && plot.status !== PlotStatusEnum.analysed;
  return (
    <Pill
      size='small'
      style={{
        backgroundColor: getColorOfPill(isOutdatedValue),
        color: getTextColorOfPill(plot.status),
      }}
      leftAdornment={icon}
    >
      {plotFact ? display : t('global.misc.comingSoon')}
    </Pill>
  );
};

const BiodiversityZone = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.biodiversity_buffer_zone_pct], plot.id);
    const biodiversityZone = getFactFromFactArray<number>(
      fact,
      IndicatorName.biodiversity_buffer_zone_pct,
    ) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={biodiversityZone} icon={<BiodiversityIcon size={12} />} />;
  },
  <PendingPill />,
);

const CarbonStorage = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.permanent_soil_carbon_per_ha], plot.id);
    const carbonStorage = getFactFromFactArray<number>(
      fact,
      IndicatorName.permanent_soil_carbon_per_ha,
    ) as PlotReportFactElement<number>;

    const formattedCarbonStorage = formatFactKilogramsToTonnes(carbonStorage) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={formattedCarbonStorage} icon={<Co2Icon size={12} />} />;
  },
  <PendingPill />,
);

const AboveGroundCarbonStorage = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.permanent_soil_carbon_per_ha], plot.id);

    const aboveGroundCarbonStorage = getFactFromFactArray<number>(
      fact,
      IndicatorName.permanent_soil_carbon_per_ha,
    ) as PlotReportFactElement<number>;
    const formattedAboveGroundCarbonStorage = formatFactKilogramsToTonnes(
      aboveGroundCarbonStorage,
    ) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={formattedAboveGroundCarbonStorage} icon={<Co2Icon size={12} />} />;
  },
  <PendingPill />,
);

const WaterHoldingCapacity = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.water_fc_per_ha], plot.id);
    const waterHoldingCapacity = getFactFromFactArray<number>(
      fact,
      IndicatorName.water_fc_per_ha,
    ) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={waterHoldingCapacity} icon={<WaterIcon size={12} />} />;
  },
  <PendingPill />,
);

const CanopyCover = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.permanent_soil_carbon_per_ha], plot.id);
    const canopyCover = getFactFromFactArray<number>(
      fact,
      IndicatorName.permanent_soil_carbon_per_ha,
    ) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={canopyCover} icon={<ForestryIcon size={12} />} />;
  },
  <PendingPill />,
);

const CanopyHeight = withSuspenseBoundary(
  (plot: Plot) => {
    const fact = usePlotFacts([IndicatorName.permanent_soil_carbon_per_ha], plot.id);
    const canopyHeight = getFactFromFactArray<number>(
      fact,
      IndicatorName.permanent_soil_carbon_per_ha,
    ) as PlotReportFactElement<number>;

    return <BaseInfoPill plot={plot} plotFact={canopyHeight} icon={<ForestryIcon size={12} />} />;
  },
  <PendingPill />,
);

type InfoBoxProps = StackProps;

const InfoBox: FC<InfoBoxProps> = ({ className, ...delegated }) => (
  <Stack className={cn('min-w-0', className)} {...delegated} />
);

const InfoTitle: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...delegated }) => (
  <span
    className={cn('typography-overline block truncate pb-2 text-left text-text-disabled', className)}
    {...delegated}
  />
);

const InfoText: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...delegated }) => (
  <span className={cn('typography-h4 block truncate whitespace-nowrap text-left', className)} {...delegated} />
);
